import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "../supabaseClient";
import {
  UploadCloud,
  CheckCircle,
  Clock,
  ShieldCheck,
  Sparkles,
  Mail,
  FileText,
  ArrowRight,
} from "lucide-react";

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || "https://jobzam-backend.onrender.com";

export default function ExpertReviewPage() {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    job_title: "",
    job_description: "",
    notes: "",
  });

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user?.email) {
          setFormData((prev) => ({
            ...prev,
            email: session.user.email,
          }));
        }
      } catch (error) {
        console.error("Failed to load user email:", error);
      }
    };

    loadUserEmail();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email) {
        setFormData((prev) => ({
          ...prev,
          email: session.user.email,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        alert("Please login first.");
        return;
      }

      const loggedInEmail = session.user.email;

      if (!formData.full_name || !formData.job_title) {
        alert("Please fill in your name and target job role.");
        return;
      }

      if (!resume) {
        alert("Please upload your CV.");
        return;
      }

      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(resume.type)) {
        alert("Please upload a PDF, DOC, or DOCX file.");
        return;
      }

      const fileExt = resume.name.split(".").pop();

      const safeFileName = resume.name
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9.-]/g, "");

      const fileName = `${Date.now()}-${safeFileName || `cv.${fileExt}`}`;
      const filePath = `${session.user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("expert-cv-reviews")
        .upload(filePath, resume, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        alert("CV upload failed. Please try again.");
        return;
      }

      const { data: insertedReview, error: insertError } = await supabase
        .from("expert_cv_reviews")
        .insert([
          {
            user_id: session.user.id,
            email: loggedInEmail,
            full_name: formData.full_name,
            job_title: formData.job_title,
            job_description: formData.job_description,
            notes: formData.notes,
            resume_file_url: filePath,
            payment_status: "pending",
            review_status: "pending",
          },
        ])
        .select()
        .single();

      if (insertError || !insertedReview) {
        console.error("Insert error:", insertError);
        alert("Failed to save your review request.");
        return;
      }

      const response = await fetch(
        `${BACKEND_URL}/create-expert-review-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            review_id: insertedReview.id,
          }),
        }
      );

      const checkoutData = await response.json();

      if (!response.ok) {
        console.error("Checkout error:", checkoutData);
        alert(checkoutData.detail || "Checkout failed.");
        return;
      }

      if (checkoutData.checkout_url) {
        window.location.href = checkoutData.checkout_url;
        return;
      }

      alert("Stripe checkout failed.");
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-32 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-12"
        >

          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
            Expert CV Review
          </h1>

          <p className="mt-5 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get your CV professionally reviewed and improved by our expert team,
            tailored to your target role and delivered within 1–2 working days.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="lg:col-span-3 bg-white/80 backdrop-blur-xl border border-white shadow-2xl rounded-3xl p-6 md:p-8"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-950">
                Submit your CV for review
              </h2>

              <p className="text-gray-500 mt-2">
                Tell us your target role and upload your current CV.
              </p>
            </div>

            <div className="space-y-5">
              <input
                type="text"
                name="full_name"
                placeholder="Full name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-gray-900 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition"
              />

              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                readOnly
                className="w-full rounded-2xl border border-gray-200 bg-gray-100 px-5 py-4 text-gray-500 outline-none cursor-not-allowed"
              />

              <input
                type="text"
                name="job_title"
                placeholder="Target job role, e.g. Data Analyst"
                value={formData.job_title}
                onChange={handleChange}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-gray-900 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition"
              />

              <textarea
                name="job_description"
                placeholder="Paste job description here (optional but recommended)"
                value={formData.job_description}
                onChange={handleChange}
                rows={5}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-gray-900 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition resize-none"
              />

              <textarea
                name="notes"
                placeholder="Anything specific you want us to improve? e.g. ATS score, summary, work experience, career gap"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-gray-900 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition resize-none"
              />

              <label className="block cursor-pointer">
                <div className="rounded-3xl border-2 border-dashed border-blue-200 bg-blue-50/60 hover:bg-blue-50 transition p-8 text-center">
                  <UploadCloud className="w-10 h-10 text-blue-600 mx-auto mb-3" />

                  <p className="font-semibold text-gray-900">
                    {resume ? resume.name : "Upload your CV"}
                  </p>

                  <p className="text-sm text-gray-500 mt-1">
                    PDF, DOC or DOCX accepted
                  </p>
                </div>

                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setResume(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-xl border border-white rounded-3xl p-6 shadow-xl">
              <h3 className="font-bold text-gray-950 text-lg mb-4">
                What’s included
              </h3>

              <div className="space-y-4 text-gray-700">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Personalised human CV review</span>
                </div>

                <div className="flex gap-3">
                  <FileText className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <span>ATS-focused wording and structure improvements</span>
                </div>

                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                  <span>1–2 working day turnaround</span>
                </div>

                <div className="flex gap-3">
                  <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <span>Updated CV sent directly to your email</span>
                </div>

                <div className="flex gap-3">
                  <ShieldCheck className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                  <span>1 month of JobZam Premium included</span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-blue-50 border border-blue-100 p-5">
              <p className="text-sm text-blue-900 leading-relaxed">
                This service is ideal if you want an expert to refine your CV
                before applying for competitive roles, graduate schemes,
                employer licence checks, or career-change opportunities.
              </p>
            </div>

            <div className="bg-gray-950 text-white rounded-3xl p-7 shadow-2xl relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-blue-500/30 rounded-full blur-2xl" />

              <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-purple-500/30 rounded-full blur-2xl" />

              <div className="relative">
                <p className="text-blue-200 font-semibold mb-2">
                  Expert Review Package
                </p>

                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-blue-100 text-xs font-semibold mb-4">
                  <Sparkles className="w-3 h-3" />
                  Limited Time 50% Launch Offer
                </div>

                <div className="flex items-end gap-3 mb-2">
                  <span className="text-gray-400 line-through text-2xl">
                    £49.99
                  </span>

                  <span className="text-5xl font-extrabold">£24.99</span>
                </div>

                <p className="text-gray-300 mb-6">
                  Includes human CV review + 1 month JobZam Premium.
                </p>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 rounded-2xl font-bold transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading
                    ? "Redirecting to Payment..."
                    : "Continue to Payment"}

                  {!loading && <ArrowRight className="w-5 h-5" />}
                </button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  Secure payment. CV delivered by email within 1–2 working days.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
